/**
 * Returns the dot-notation path of the first field with a validation error.
 *
 * @param {Object} errors - React Hook Form errors object
 * @param {string} [prefix=''] - Path prefix used during recursion
 * @returns {string|null} The first errored field path, or null if none found
 */
export function getFirstErrorFieldName(errors, prefix = '') {
    if (!errors || typeof errors !== 'object') {
        return null;
    }

    for (const key of Object.keys(errors)) {
        const value = errors[key];
        const path = prefix ? `${prefix}.${key}` : key;

        if (value?.message) {
            return path;
        }

        if (value && typeof value === 'object') {
            const nested = getFirstErrorFieldName(value, path);
            if (nested) {
                return nested;
            }
        }
    }

    return null;
}

function fieldNameToKebabCase(fieldName) {
    return fieldName.replace(/([A-Z])/g, '-$1').toLowerCase();
}

/**
 * Locates a form field element in the DOM by its React Hook Form field name.
 *
 * @param {string} fieldName - The field name/path from the errors object
 * @returns {HTMLElement|null} The matching element, or null if not found
 */
export function findFieldElement(fieldName) {
    const kebabName = fieldNameToKebabCase(fieldName);

    return (
        document.querySelector(`[name="${fieldName}"]`) ??
        document.getElementById(fieldName) ??
        document.getElementById(kebabName) ??
        document.getElementById(`${fieldName}-input-field`) ??
        document.getElementById(`${fieldName}-container`)
    );
}

/**
 * Focuses and scrolls to the first field with a validation error.
 *
 * @param {Object} formErrors - React Hook Form errors object from onInvalid
 * @param {Function} setFocus - React Hook Form setFocus function
 */
export function scrollToFirstError(formErrors, setFocus) {
    const fieldName = getFirstErrorFieldName(formErrors);
    if (!fieldName) {
        return;
    }

    setFocus(fieldName);

    const element = findFieldElement(fieldName);
    if (!element) {
        return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (typeof element.focus === 'function') {
        element.focus({ preventScroll: true });
    }
}
